import { PlasticsMoldHistoryEvent } from '../models/plastics-mold-history-event.model';

type EditableHistoryEvent = Pick<
    PlasticsMoldHistoryEvent,
    'title' | 'description' | 'occurredOn' | 'files'
>;

// Leave room for the surrounding change-log object when it is JSON encoded into
// Appwrite's 20,000-character string-array column.
const MAX_AUDIT_TEXT_LENGTH = 18_000;

export function buildPlasticsMoldHistoryEventChangeText(
    previous: EditableHistoryEvent,
    next: EditableHistoryEvent,
    comment = '',
): string {
    const lines: string[] = [];
    const trimmedComment = comment.trim();
    if (trimmedComment) lines.push(trimmedComment);

    if (previous.title !== next.title) {
        lines.push(`title: ${auditValue(previous.title)} → ${auditValue(next.title)}`);
    }
    if (dateKey(previous.occurredOn) !== dateKey(next.occurredOn)) {
        lines.push(`event date: ${formatDate(previous.occurredOn)} → ${formatDate(next.occurredOn)}`);
    }
    if (previous.description !== next.description) {
        lines.push(`description: ${auditValue(previous.description)} → ${auditValue(next.description)}`);
    }

    const previousFilesById = new Map((previous.files || []).map(file => [file.id, file]));
    const nextFilesById = new Map((next.files || []).map(file => [file.id, file]));
    for (const file of next.files || []) {
        if (!previousFilesById.has(file.id)) lines.push(`attachment added: ${file.name}`);
    }
    for (const file of previous.files || []) {
        if (!nextFilesById.has(file.id)) lines.push(`attachment removed: ${file.name}`);
    }

    const text = lines.join('\n');
    if (text.length <= MAX_AUDIT_TEXT_LENGTH) return text;
    return `${text.slice(0, MAX_AUDIT_TEXT_LENGTH - 26)}\n… audit details truncated`;
}

export function plasticsMoldHistoryEventHasChanges(
    previous: EditableHistoryEvent,
    next: EditableHistoryEvent,
): boolean {
    return buildPlasticsMoldHistoryEventChangeText(previous, next).length > 0;
}

function auditValue(value: string): string {
    return value.trim() || 'none';
}

function dateKey(value: Date): string {
    const date = new Date(value);
    return [date.getFullYear(), date.getMonth() + 1, date.getDate()].join('-');
}

function formatDate(value: Date): string {
    const date = new Date(value);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${month}/${day}/${date.getFullYear()}`;
}
